import { useState, useEffect, useCallback } from "react";
import {
  getAdminLawyersExequaturOverview,
  getAllVerificationRequests,
  getVerificationRequestHistory,
  updateVerificationStatus,
  adminSetVerificationDirect,
  logAdminActivity,
  createNotification,
  isUserAdmin,
  type AdminLawyerExequaturRow,
  type VerificationRequest,
  type VerificationRequestWithProfile,
} from "@/shared/db";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../contexts/AuthContext";

export type ExequaturDisplayStatus =
  | "no_enviado"
  | "pendiente"
  | "verificado"
  | "invalido"
  | "rechazado"
  | "en_revision";

export interface ExequaturFilters {
  exequatur_status?: ExequaturDisplayStatus;
  country?: string;
  auto_verified?: boolean;
  search?: string;
}

export function useExequaturVerification(
  view: "users" | "requests" = "users",
  filters: ExequaturFilters = {}
) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [lawyers, setLawyers] = useState<AdminLawyerExequaturRow[]>([]);
  const [requests, setRequests] = useState<VerificationRequestWithProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLawyers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAdminLawyersExequaturOverview({
        ...filters,
        limit: 100,
      });
      setLawyers(data);
    } catch (err: unknown) {
      console.error("Error fetching lawyers exequatur:", err);
      const msg =
        err instanceof Error ? err.message : t("admin.exequatur.errors.fetchFailed");
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [filters, t]);

  const statusToDb = (s?: ExequaturDisplayStatus): string | undefined => {
    if (!s) return undefined;
    const map: Record<ExequaturDisplayStatus, string | undefined> = {
      no_enviado: undefined,
      pendiente: "pending",
      verificado: "approved",
      invalido: "rejected",
      rechazado: "rejected",
      en_revision: "in_review",
    };
    return map[s];
  };

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllVerificationRequests({
        status: statusToDb(filters.exequatur_status),
        limit: 100,
      });
      setRequests(data ?? []);
    } catch (err: unknown) {
      console.error("Error fetching verification requests:", err);
      const msg =
        err instanceof Error ? err.message : t("admin.exequatur.errors.fetchFailed");
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [filters.exequatur_status, t]);

  useEffect(() => {
    if (view === "users") {
      fetchLawyers();
    } else {
      fetchRequests();
    }
  }, [view, fetchLawyers, fetchRequests]);

  const refetch = useCallback(() => {
    if (view === "users") fetchLawyers();
    else fetchRequests();
  }, [view, fetchLawyers, fetchRequests]);

  const notifyLawyerExequatur = async (
    lawyerId: string,
    type: "verification_approved" | "verification_rejected" | "exequatur_invalid" | "missing_exequatur",
    title: string,
    message: string,
    options?: { data?: Record<string, unknown>; link?: string }
  ) => {
    if (await isUserAdmin(lawyerId)) return;
    await createNotification({
      userId: lawyerId,
      type,
      title,
      message,
      data: options?.data,
      link: options?.link ?? "/dashboard/lawyer/profile/edit",
    });
  };

  const approveRequest = async (
    requestId: string,
    lawyerId: string
  ): Promise<boolean> => {
    if (!user?.id) return false;
    try {
      await updateVerificationStatus(requestId, "approved", user.id);
      await logAdminActivity(user.id, "verification_approved", "verification_request", requestId, {
        lawyer_id: lawyerId,
      });
      await notifyLawyerExequatur(
        lawyerId,
        "verification_approved",
        t("admin.exequatur.notifications.approvedTitle"),
        t("admin.exequatur.notifications.approvedMessage"),
      );
      toast.success(t("admin.exequatur.actions.approved"));
      refetch();
      return true;
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : t("admin.exequatur.errors.updateFailed");
      toast.error(msg);
      return false;
    }
  };

  const rejectRequest = async (
    requestId: string,
    lawyerId: string,
    reason: string
  ): Promise<boolean> => {
    if (!user?.id || !reason?.trim()) {
      toast.error(t("admin.exequatur.errors.reasonRequired"));
      return false;
    }
    try {
      await updateVerificationStatus(
        requestId,
        "rejected",
        user.id,
        undefined,
        reason.trim()
      );
      await logAdminActivity(user.id, "verification_rejected", "verification_request", requestId, {
        lawyer_id: lawyerId,
        reason: reason.trim(),
      });
      await notifyLawyerExequatur(
        lawyerId,
        "verification_rejected",
        t("admin.exequatur.notifications.rejectedTitle"),
        t("admin.exequatur.notifications.rejectedMessage", { reason: reason.trim() }),
        { data: { reason: reason.trim() } },
      );
      toast.success(t("admin.exequatur.actions.rejected"));
      refetch();
      return true;
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : t("admin.exequatur.errors.updateFailed");
      toast.error(msg);
      return false;
    }
  };

  const markInvalid = async (
    requestId: string,
    lawyerId: string,
    reason: string
  ): Promise<boolean> => {
    if (!user?.id || !reason?.trim()) {
      toast.error(t("admin.exequatur.errors.reasonRequired"));
      return false;
    }
    try {
      await updateVerificationStatus(
        requestId,
        "rejected",
        user.id,
        undefined,
        `inválido: ${reason.trim()}`
      );
      await logAdminActivity(user.id, "exequatur_invalid", "verification_request", requestId, {
        lawyer_id: lawyerId,
        reason: reason.trim(),
      });
      await notifyLawyerExequatur(
        lawyerId,
        "exequatur_invalid",
        t("admin.exequatur.notifications.invalidTitle"),
        t("admin.exequatur.notifications.invalidMessage", { reason: reason.trim() }),
        { data: { reason: reason.trim() } },
      );
      toast.success(t("admin.exequatur.actions.markedInvalid"));
      refetch();
      return true;
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : t("admin.exequatur.errors.updateFailed");
      toast.error(msg);
      return false;
    }
  };

  const approveOrRejectDirect = async (
    lawyerId: string,
    status: "approved" | "rejected",
    reason?: string
  ): Promise<boolean> => {
    if (!user?.id) return false;
    try {
      await adminSetVerificationDirect(lawyerId, status, user.id, reason);
      if (status === "approved") {
        await logAdminActivity(user.id, "verification_approved", "lawyer", lawyerId, { direct: true });
        await notifyLawyerExequatur(
          lawyerId,
          "verification_approved",
          t("admin.exequatur.notifications.approvedTitle"),
          t("admin.exequatur.notifications.approvedMessage"),
        );
        toast.success(t("admin.exequatur.actions.approved"));
      } else {
        await logAdminActivity(user.id, "verification_rejected", "lawyer", lawyerId, { reason, direct: true });
        await notifyLawyerExequatur(
          lawyerId,
          "verification_rejected",
          t("admin.exequatur.notifications.rejectedTitle"),
          t("admin.exequatur.notifications.rejectedMessage", { reason: reason ?? "" }),
          { data: { reason: reason ?? "" } },
        );
        toast.success(t("admin.exequatur.actions.rejected"));
      }
      refetch();
      return true;
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : t("admin.exequatur.errors.updateFailed");
      toast.error(msg);
      return false;
    }
  };

  const sendToReview = async (requestId: string, lawyerId: string): Promise<boolean> => {
    if (!user?.id) return false;
    try {
      await updateVerificationStatus(requestId, "in_review", user.id);
      await logAdminActivity(user.id, "verification_sent_to_review", "verification_request", requestId, {
        lawyer_id: lawyerId,
      });
      toast.success(t("admin.exequatur.actions.sentToReview"));
      refetch();
      return true;
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : t("admin.exequatur.errors.updateFailed");
      toast.error(msg);
      return false;
    }
  };

  const notifyMissingExequatur = async (lawyerIds: string[]): Promise<number> => {
    if (!user?.id || lawyerIds.length === 0) return 0;
    let sent = 0;
    for (const lawyerId of lawyerIds) {
      try {
        await notifyLawyerExequatur(
          lawyerId,
          "missing_exequatur",
          t("admin.exequatur.notifications.missingTitle"),
          t("admin.exequatur.notifications.missingMessage"),
        );
        sent++;
      } catch (err) {
        console.error("Error notifying lawyer:", lawyerId, err);
      }
    }
    if (sent > 0) {
      await logAdminActivity(user.id, "notify_missing_exequatur", "lawyer", undefined, {
        lawyer_ids: lawyerIds,
        count: sent,
      });
    }
    return sent;
  };

  return {
    lawyers,
    requests,
    loading,
    error,
    refetch,
    approveRequest,
    rejectRequest,
    markInvalid,
    sendToReview,
    approveOrRejectDirect,
    notifyMissingExequatur,
  };
}

export function useVerificationRequestDetail(lawyerId: string | null) {
  const [history, setHistory] = useState<VerificationRequest[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!lawyerId) {
      setHistory([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getVerificationRequestHistory(lawyerId)
      .then((data) => {
        if (!cancelled) setHistory(data ?? []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [lawyerId]);

  return { history, loading };
}
